import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ReactFlow,
  ReactFlowProvider,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import LogoLoader from "../../common/LogoLoader";
import { useCapabilityMap } from "../../../hooks/queries/useCapabilityMap";
import { monospaceStyle } from "../../../theme";
import { useCapturing } from "../../../utils/export/ExportStage";
import {
  CapabilityGrain,
  CapabilityMap,
  CapabilityMapNode,
  CapabilityMapPair,
} from "../../../types/analytics";
import {
  ANALYTICS_VIEW_LABELS,
  CAPABILITY_GRAINS,
  selectionOf,
  toCapabilityMapParams,
} from "../analyticsState";
import { AnalyticsPaneProps } from "../analyticsPanes";
import { SEGMENT_SX, TOOL_BUTTON_SX } from "../../ValueChain/ValueChainControls";
import { areaColourOf } from "./capabilityMapColours";
import {
  CapabilityMapLayout,
  LABEL_WIDTH,
  LayoutRoom,
  layoutCapabilityMap,
} from "./capabilityMapLayout";
import CapabilityMapPanel from "./CapabilityMapPanel";
import {
  DotFlowNode,
  DotLook,
  DotNode,
  PairEdge,
  PairFlowEdge,
  RegionFlowNode,
  RegionNode,
  counted,
  dotSx,
  lineWidthOf,
  nodeName,
  pairReading,
  percent,
} from "./CapabilityMapParts";

type MapFlowNode = RegionFlowNode | DotFlowNode;

const NODE_TYPES = { region: RegionNode, dot: DotNode };
const EDGE_TYPES = { pair: PairEdge };
// The whole map in view, with a little air; a small map is enlarged a little
const FIT_VIEW_OPTIONS = { padding: 0.03, maxZoom: 1.25 };
// The share of the canvas the map is laid out for, the rest being that air,
// and the step (px) its size is taken in
const ROOM_SHARE = 0.94;
const ROOM_STEP = 40;

export const GRAIN_LABELS: Record<CapabilityGrain, string> = {
  core: "Core",
  condition: "With condition",
  mode: "With mode",
};

// What the floors left off the map, as a caption
export const omittedCaption = (map: CapabilityMap): string =>
  [
    counted(map.omitted_pairs, "pair") +
      " under the similarity floor not drawn",
    ...(map.omitted_nodes > 0
      ? [
          `${map.omitted_nodes} ${
            map.omitted_nodes === 1 ? "capability" : "capabilities"
          } beyond the top ${map.floors.top_n} not drawn`,
        ]
      : []),
  ].join(" · ");

// What the map answers, in a sentence, above it
export const MAP_LEAD =
  "What the products in this scope are said to be able to do, and which " +
  "capabilities come together in the same products.";

// A floor the read reports, as a chip of the controls row: it is not a
// switch. What it does to the drawing is said where the pointer rests on it.
const FloorChip: React.FC<{
  name: string;
  tip: string;
  children: React.ReactNode;
}> = ({ name, tip, children }) => (
  <Tooltip title={tip} describeChild>
    <Chip aria-label={name} sx={{ height: 32 }} label={children} />
  </Tooltip>
);

// What the two floors do to the drawing, in a sentence each
export const similarityTip = (floors: CapabilityMap["floors"]): string =>
  `A line is drawn where at least ${percent(floors.min_jaccard)} of the ` +
  "products said to have either capability are said to have both " +
  `(a Jaccard similarity of ${floors.min_jaccard.toFixed(2)} or more).`;

export const supportTip = (floors: CapabilityMap["floors"]): string =>
  `A capability on fewer than ${counted(floors.min_support, "product")} is ` +
  "hatched and a line on fewer is dashed: too few to read much into.";

// A dot of the legend, in the grey of no effect area
const LegendDot: React.FC<{ look: DotLook; size?: number }> = ({
  look,
  size = 14,
}) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        ...dotSx(theme, look, look === "thin", theme.palette.chart.neutral),
        display: "inline-block",
        flexShrink: 0,
        width: size,
        height: size,
      }}
    />
  );
};

// A line of the legend, of the width of a pair of the given Jaccard
const LegendLine: React.FC<{ jaccard: number; dashed?: boolean }> = ({
  jaccard,
  dashed = false,
}) => {
  const theme = useTheme();
  return (
    <svg aria-hidden width={22} height={8} style={{ flexShrink: 0 }}>
      <line
        x1={2}
        y1={4}
        x2={20}
        y2={4}
        stroke={theme.palette.chart.neutral}
        strokeWidth={lineWidthOf(jaccard)}
        strokeLinecap="round"
        strokeDasharray={dashed ? "5 5" : undefined}
      />
    </svg>
  );
};

// What the marks of the map mean, under it, in plain words. An entry whose
// mark is nowhere on the map is left out.
const Legend: React.FC<{ map: CapabilityMap; colours: string[] }> = ({
  map,
  colours,
}) => {
  const entry = { display: "inline-flex", alignItems: "center", gap: "6px" };
  const few = `fewer than ${counted(map.floors.min_support, "product")}`;
  return (
    <Box
      component="ul"
      aria-label="Legend"
      sx={{
        m: 0,
        p: 0,
        listStyle: "none",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: "22px",
        rowGap: 1,
        fontSize: 12,
        color: "text.secondary",
      }}
    >
      <Box component="li" sx={entry}>
        <Box
          component="span"
          aria-hidden
          sx={{ display: "inline-flex", alignItems: "center", gap: "3px" }}
        >
          <LegendDot look="claimed" size={8} />
          <LegendDot look="claimed" size={12} />
          <LegendDot look="claimed" size={17} />
        </Box>
        circle = a capability · larger = more products said to have it
      </Box>
      {map.nodes.some((node) => node.need_count > 0) && (
        <Box component="li" sx={entry}>
          <LegendDot look="need" />
          amber dashes = also stated as a need, required or lacking
        </Box>
      )}
      {map.nodes.some((node) => node.thin) && (
        <Box component="li" sx={entry}>
          <LegendDot look="thin" />
          hatched = {few}
        </Box>
      )}
      <Box component="li" sx={entry}>
        <Box
          component="span"
          aria-hidden
          sx={{ display: "inline-flex", alignItems: "center", gap: "3px" }}
        >
          <LegendLine jaccard={0.1} />
          <LegendLine jaccard={0.7} />
        </Box>
        line = the same products have both · heavier = more of them do
      </Box>
      {map.pairs.some((pair) => pair.thin) && (
        <Box component="li" sx={entry}>
          <LegendLine jaccard={0.2} dashed />
          dashed = {few} have both
        </Box>
      )}
      <Box component="li" sx={entry}>
        <Box
          component="span"
          aria-hidden
          sx={{ display: "inline-flex", alignItems: "center", gap: "3px" }}
        >
          {colours.map((colour) => (
            <Box
              key={colour}
              component="span"
              data-testid="legend-colour"
              sx={{
                width: 9,
                height: 9,
                borderRadius: "2px",
                backgroundColor: colour,
              }}
            />
          ))}
        </Box>
        colour and box = effect area
      </Box>
    </Box>
  );
};

// The pair that says the most: the most alike of those at or above the
// floor, else of all
export const strongestPair = (map: CapabilityMap): CapabilityMapPair | null => {
  const sound = map.pairs.filter((pair) => !pair.thin);
  return (sound.length > 0 ? sound : map.pairs).reduce<CapabilityMapPair | null>(
    (best, pair) => (best === null || pair.jaccard > best.jaccard ? pair : best),
    null,
  );
};

// The strongest pair of the map read out, as an example of what a line says;
// its two names select their nodes
const StrongestLink: React.FC<{
  map: CapabilityMap;
  onSelect: (id: string) => void;
}> = ({ map, onSelect }) => {
  const pair = strongestPair(map);
  const a = pair && map.nodes.find((node) => node.id === pair.a);
  const b = pair && map.nodes.find((node) => node.id === pair.b);
  if (!pair || !a || !b) {
    return null;
  }
  const either = a.product_count + b.product_count - pair.support;
  const name = (node: CapabilityMapNode) => (
    <MuiLink
      component="button"
      type="button"
      underline="hover"
      onClick={() => onSelect(node.id)}
      sx={{ font: "inherit", verticalAlign: "baseline" }}
    >
      {nodeName(node)}
    </MuiLink>
  );
  return (
    <Typography
      aria-label="Strongest link"
      sx={{ fontSize: 12.5, lineHeight: 1.5, color: "text.secondary" }}
    >
      Strongest link: {name(a)} + {name(b)}.{" "}
      {counted(pair.support, "product")} {pair.support === 1 ? "is" : "are"}{" "}
      said to have both, {percent(pair.jaccard)} of the {either} said to have
      either.
    </Typography>
  );
};

interface CanvasProps {
  layout: CapabilityMapLayout;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  // Counts the asks to fit the view: a new count fits it again
  fitRequest: number;
}

// The regions, dots and pair lines of a layout; the layout is never run
// again by a render, only by a new response
const MapCanvas: React.FC<CanvasProps> = ({
  layout,
  selectedId,
  onSelect,
  fitRequest,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<MapFlowNode>([]);
  const { fitView } = useReactFlow<MapFlowNode>();
  // Whether every node is measured: until then the canvas shows nothing,
  // however fast the response came
  const initialized = useNodesInitialized();
  // The selection in force, read when the layout builds the nodes
  const selected = useRef(selectedId);
  selected.current = selectedId;
  // The node the pointer or the keyboard is on
  const [pointedId, setPointedId] = useState<string | null>(null);
  const theme = useTheme();
  const { chart } = theme.palette;

  const placedById = useMemo(
    () => new Map(layout.nodes.map((node) => [node.node.id, node])),
    [layout],
  );
  // The node whose pairs are lit: the one pointed at, else the selected one
  const onMap = (id: string | null) =>
    id !== null && placedById.has(id) ? id : null;
  const focusId = onMap(pointedId) ?? onMap(selectedId);

  const partners = useMemo(() => {
    const set = new Set<string>();
    if (focusId) {
      for (const line of layout.lines) {
        if (line.a === focusId) set.add(line.b);
        if (line.b === focusId) set.add(line.a);
      }
    }
    return set;
  }, [layout, focusId]);

  const pick = useCallback((id: string) => onSelect(id), [onSelect]);

  useEffect(() => {
    const regions: RegionFlowNode[] = layout.regions.map((region) => ({
      id: `region:${region.id}`,
      type: "region",
      position: { x: region.x, y: region.y },
      data: { label: region.label, colour: areaColourOf(region.id, chart) },
      style: { width: region.width, height: region.height },
      zIndex: -1,
      draggable: false,
      selectable: false,
      focusable: false,
    }));
    const dots: DotFlowNode[] = layout.nodes.map((placed) => ({
      id: placed.node.id,
      type: "dot",
      // The label is as wide as the node; the dot is centred at its top
      position: { x: placed.x - LABEL_WIDTH / 2, y: placed.y - placed.radius },
      data: {
        node: placed.node,
        radius: placed.radius,
        colour: areaColourOf(placed.regionId, chart),
        selected: false,
        dimmed: false,
        onSelect: pick,
        onPoint: setPointedId,
      },
      draggable: false,
      connectable: false,
    }));
    setNodes([...regions, ...dots]);
    window.requestAnimationFrame(() => {
      void fitView(FIT_VIEW_OPTIONS);
    });
  }, [layout, pick, setNodes, fitView, chart]);

  // The Fit control brings the whole map back into view
  useEffect(() => {
    if (fitRequest > 0) {
      void fitView(FIT_VIEW_OPTIONS);
    }
  }, [fitRequest, fitView]);

  // A change of selection or of the node pointed at lights and dims in place
  useEffect(() => {
    setNodes((current) =>
      current.map((node) => {
        if (node.type !== "dot") {
          return node;
        }
        const isSelected = node.id === selectedId;
        const dimmed =
          focusId !== null &&
          node.id !== focusId &&
          !isSelected &&
          !partners.has(node.id);
        const data = node.data as DotFlowNode["data"];
        return data.selected === isSelected && data.dimmed === dimmed
          ? node
          : { ...node, data: { ...data, selected: isSelected, dimmed } };
      }),
    );
  }, [layout, chart, selectedId, focusId, partners, setNodes]);

  const edges = useMemo<PairFlowEdge[]>(
    () =>
      layout.lines.flatMap((line) => {
        const a = placedById.get(line.a);
        const b = placedById.get(line.b);
        if (!a || !b) {
          return [];
        }
        const active = focusId !== null && (line.a === focusId || line.b === focusId);
        return [
          {
            id: line.id,
            type: "pair",
            source: line.a,
            target: line.b,
            selectable: false,
            focusable: false,
            data: {
              x1: line.x1,
              y1: line.y1,
              x2: line.x2,
              y2: line.y2,
              a: line.a,
              b: line.b,
              jaccard: line.jaccard,
              support: line.support,
              thin: line.thin,
              from: areaColourOf(a.regionId, chart),
              to: areaColourOf(b.regionId, chart),
              reading: pairReading(a.node, b.node, line.support, line.jaccard),
              active,
              dimmed: focusId !== null && !active,
            },
          },
        ];
      }),
    [layout, placedById, focusId, chart],
  );

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onPaneClick={() => onSelect(null)}
        nodesDraggable={false}
        nodesConnectable={false}
        fitView
        fitViewOptions={FIT_VIEW_OPTIONS}
        minZoom={0.2}
        maxZoom={1.5}
        colorMode={theme.palette.mode}
        proOptions={{ hideAttribution: true }}
        style={{ backgroundColor: "transparent" }}
      />
      {!initialized && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            p: 3,
            backgroundColor: "background.paper",
          }}
        >
          <LogoLoader size="view" surface="paper" label="Loading the capability map" />
        </Box>
      )}
    </>
  );
};

// The capability map: the capabilities of the scope's products as dots in
// the regions of their effect areas, the pairs claimed together as lines
const CapabilityMapView: React.FC<AnalyticsPaneProps> = ({
  state,
  onChange,
  onOpenEntity,
}) => {
  const theme = useTheme();
  // In an exported image: the map, its floors and its legend, no controls
  const capturing = useCapturing();
  const params = useMemo(() => toCapabilityMapParams(state), [state]);
  const read = useCapabilityMap(params);
  const map: CapabilityMap | null = read.data ?? null;
  // The size of the canvas, in steps, so that a small resize lays out nothing
  const surface = useRef<HTMLDivElement | null>(null);
  const [room, setRoom] = useState<LayoutRoom | null>(null);
  useEffect(() => {
    const element = surface.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const measure = () => {
      const width = Math.floor((element.clientWidth * ROOM_SHARE) / ROOM_STEP) * ROOM_STEP;
      const height = Math.floor((element.clientHeight * ROOM_SHARE) / ROOM_STEP) * ROOM_STEP;
      setRoom((current) =>
        width <= 0 || height <= 0
          ? null
          : current && current.width === width && current.height === height
            ? current
            : { width, height },
      );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const layout = useMemo(
    () => (map ? layoutCapabilityMap(map, room) : null),
    [map, room],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const grain = state.settings["capability-map"].grain;
  const [fitRequest, setFitRequest] = useState(0);
  // Whether there is a canvas to fit
  const drawn = map !== null && map.nodes.length > 0;

  // A selection that is no longer on the map is none
  const node = map?.nodes.find((candidate) => candidate.id === selectedId) ?? null;
  useEffect(() => {
    if (selectedId !== null && map && !node) {
      setSelectedId(null);
    }
  }, [selectedId, map, node]);

  return (
    <Box
      component="section"
      aria-label={ANALYTICS_VIEW_LABELS["capability-map"]}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: "14px",
      }}
    >
      {/* What the map answers, and the controls at the far end of the row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 3,
          rowGap: 1,
        }}
      >
        <Typography
          aria-label="What the map shows"
          sx={{
            flex: "1 1 340px",
            minWidth: 0,
            fontSize: 13,
            lineHeight: 1.5,
            color: "text.secondary",
          }}
        >
          {MAP_LEAD}
        </Typography>
        <Box
          role="toolbar"
          aria-label="Capability map controls"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            ml: "auto",
          }}
        >
          {!capturing && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={grain}
              aria-label="Grain"
              onChange={(_event, next: CapabilityGrain | null) => {
                if (next) {
                  onChange({
                    settings: {
                      ...state.settings,
                      "capability-map": { ...state.settings["capability-map"], grain: next },
                    },
                  });
                }
              }}
            >
              {CAPABILITY_GRAINS.map((value) => (
                <ToggleButton
                  key={value}
                  value={value}
                  sx={{ ...SEGMENT_SX, height: 32, px: 1.25, fontSize: "12.5px" }}
                >
                  {GRAIN_LABELS[value]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          {map && (
            <>
              <FloorChip name="Similarity floor" tip={similarityTip(map.floors)}>
                Lines from{" "}
                <Box component="span" sx={monospaceStyle}>
                  {percent(map.floors.min_jaccard)}
                </Box>{" "}
                overlap
              </FloorChip>
              <FloorChip name="Node floor" tip={supportTip(map.floors)}>
                Faint under{" "}
                <Box component="span" sx={monospaceStyle}>
                  {map.floors.min_support}
                </Box>{" "}
                products
              </FloorChip>
            </>
          )}
          {!capturing && (
            <Button
              variant="outlined"
              size="small"
              disabled={!drawn}
              onClick={() => setFitRequest((count) => count + 1)}
              sx={TOOL_BUTTON_SX}
            >
              Fit
            </Button>
          )}
        </Box>
      </Box>

      <Box
        ref={surface}
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 560,
          display: "flex",
          border: 1,
          borderColor: "divider",
          borderRadius: (theme) => `${theme.radius.surface}px`,
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        {!map || !layout ? (
          <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 3 }}>
            {read.isLoading ? (
              <LogoLoader size="view" surface="paper" label="Loading the capability map" />
            ) : (
              <Typography color="text.secondary">
                The capability map could not be loaded.
              </Typography>
            )}
          </Box>
        ) : map.nodes.length === 0 ? (
          <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 3 }}>
            <Typography color="text.secondary" align="center">
              No capability to map: the {counted(map.evidence.n, "product")} in
              this scope claim none at or above the floor of{" "}
              {counted(map.floors.min_support, "product")}.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
              <ReactFlowProvider>
                <MapCanvas
                  layout={layout}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  fitRequest={fitRequest}
                />
              </ReactFlowProvider>
            </Box>
            {node && !capturing && (
              <CapabilityMapPanel
                map={map}
                node={node}
                selection={selectionOf(state)}
                onSelect={setSelectedId}
                onOpenEntity={onOpenEntity}
              />
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {map && layout && drawn && (
          <>
            <StrongestLink map={map} onSelect={setSelectedId} />
            <Legend
              map={map}
              colours={Array.from(
                new Set(
                  layout.regions.map((region) =>
                    areaColourOf(region.id, theme.palette.chart),
                  ),
                ),
              )}
            />
          </>
        )}
        {map && (
          <Typography
            variant="caption"
            color="text.disabled"
            aria-label="Not drawn"
          >
            {omittedCaption(map)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CapabilityMapView;
